import { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useProjects } from "../data/projects";
import { useSettings, useFilterGroups } from "../data/content";
import Emphasis, { stripEmphasis } from "../components/Emphasis";
import { fullImageUrl } from "../data/sanity/client";
import { categoryToGroup, labelToSlug, projectCats } from "../data/workGroups";
import ProjectGallery from "./PagesComps/ProjectGallery/ProjectGallery";
import { ArrowUpRightIcon, ArrowRightIcon, ArrowLeftIcon } from "../components/Icons";

const META = ["client", "role", "tools", "scope"];
const META_LABELS = { client: "CLIENT", role: "ROLE", tools: "STACK", scope: "SCOPE" };

function ProjectDetail() {
  const projects = useProjects();
  const settings = useSettings();
  const groups = useFilterGroups();
  const { slug } = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();

  const idx     = projects.findIndex((p) => p.slug === slug);
  const project = projects[idx];
  const next    = projects[(idx + 1) % projects.length];

  useEffect(() => {
    if (project) {
      document.title = [stripEmphasis(project.title), settings.author].filter(Boolean).join(" — ");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, settings.author]);

  if (!project) {
    return (
      <main className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center">
        <p className="font-sans text-text-muted-light dark:text-text-muted-dark">Project not found.</p>
      </main>
    );
  }

  return (
    <main className="bg-background-light dark:bg-background-dark">
      <div className="max-w-5xl mx-auto px-6 pt-24 pb-24">

        {/* Back button */}
        <button
          onClick={() => {
            if (location.key !== "default") {
              navigate(-1);
            } else {
              const group = categoryToGroup(project, groups);
              navigate(group ? `/work?c=${labelToSlug(group)}` : "/work");
            }
          }}
          className="flex items-center gap-3 mb-6 group"
        >
          <ArrowLeftIcon size={14} className="text-text-muted-light dark:text-text-muted-dark group-hover:text-text-primary-light dark:group-hover:text-text-primary-dark transition-colors" />
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-text-muted-light dark:text-text-muted-dark group-hover:text-text-primary-light dark:group-hover:text-text-primary-dark transition-colors">
            {categoryToGroup(project, groups) ?? projectCats(project)[0]}
          </span>
        </button>

        {/* Title */}
        <h1 className="font-sans font-bold text-5xl md:text-7xl text-text-primary-light dark:text-text-primary-dark leading-tight mb-6">
          <Emphasis>{project.title}</Emphasis>
        </h1>

        {/* Description + Preview link */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <p className="font-sans text-base text-text-muted-light dark:text-text-muted-dark max-w-xl leading-relaxed">
            {project.description}
          </p>
          {project.liveUrl && (
            <a
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 flex items-center gap-1.5 font-sans text-sm text-text-muted-light dark:text-text-muted-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
            >
              Preview <ArrowUpRightIcon size={13} />
            </a>
          )}
        </div>

        {/* Separator */}
        <div className="border-t border-border-light dark:border-border-dark" />

        {/* Meta row */}
        <div className="grid grid-cols-2 md:grid-cols-4 mb-12">
          {META.map((key, i) => (
            <div key={key} className="relative py-6 px-6 first:pl-0 last:pr-0">
              {i > 0 && (
                <div className="absolute left-0 top-5 bottom-0 w-px bg-border-light dark:bg-border-dark" />
              )}
              <p className="font-sans text-2xs tracking-[0.2em] uppercase text-text-muted-light dark:text-text-muted-dark mb-1.5">
                {META_LABELS[key]}
              </p>
              <p className="font-sans text-sm text-text-primary-light dark:text-text-primary-dark leading-snug">
                {Array.isArray(project[key]) ? project[key].join(", ") : project[key]}
              </p>
            </div>
          ))}
        </div>

        {/* Images */}
        <div className="mb-24">
          <ProjectGallery
            images={[project.banner, ...(project.images || [])]
              .filter(Boolean)
              .map((image) => fullImageUrl(image))}
          />
        </div>

        {/* Divider */}
        <div className="border-t border-border-light dark:border-border-dark mb-12" />

        {/* Next project */}
        <div
          role="button"
          tabIndex={0}
          aria-label={`Next project: ${stripEmphasis(next.title)}`}
          className="flex items-center justify-between cursor-pointer group"
          onClick={() => navigate(`/work/${next.slug}`)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/work/${next.slug}`)}
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-text-muted-light dark:bg-text-muted-dark" />
              <span className="font-sans text-xs tracking-[0.3em] uppercase text-text-muted-light dark:text-text-muted-dark">
                Next Project
              </span>
            </div>

            <h2 className="font-sans font-bold text-4xl md:text-5xl text-text-primary-light dark:text-text-primary-dark leading-tight mb-4">
              <Emphasis>{next.title}</Emphasis>
            </h2>

            <div className="flex items-center gap-3 flex-wrap">
              <span className="px-3 py-1 rounded-full border border-border-light dark:border-border-dark font-sans text-xs uppercase tracking-wide text-text-muted-light dark:text-text-muted-dark">
                {projectCats(next)[0]}
              </span>
              <span className="text-text-muted-light dark:text-text-muted-dark">•</span>
              <span className="font-sans text-sm text-text-muted-light dark:text-text-muted-dark">
                {next.role}
              </span>
            </div>
          </div>

          {/* Arrow circle */}
          <div className="w-14 h-14 rounded-full border border-border-light dark:border-border-dark flex items-center justify-center ml-8 flex-shrink-0 text-text-primary-light dark:text-text-primary-dark group-hover:bg-text-primary-light dark:group-hover:bg-white group-hover:text-white dark:group-hover:text-black transition-colors duration-200">
            <ArrowRightIcon size={20} />
          </div>
        </div>

        {/* Bottom divider */}
        <div className="border-t border-border-light dark:border-border-dark mt-12" />

      </div>
    </main>
  );
}

export default ProjectDetail;
